import { supabase } from './client';
import { authStore } from '@stores/session';
import type { PortfolioSymptom } from './portfolio';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

// Canonical symptom-tagging client (migration 0049). Tags live in
// user_strain_symptoms keyed by (profile_id, strain_id, symptom_id), so a
// tag is tethered to the user + the exact product and persists whether the
// strain is in the watchlist, the portfolio, or neither. This module is
// the single write path for every surface that tags strains (/strains,
// /stock, /watchlist, /my-strains).

/**
 * Every symptom the current user has tagged, keyed by strain_id. Returns
 * an empty map when signed out. One query feeds the per-card chips and the
 * Symptoms filter across the listing pages.
 */
export async function fetchStrainSymptoms(): Promise<Map<string, PortfolioSymptom[]>> {
  const profile = authStore.profile();
  if (!profile) return new Map();
  const { data, error } = await db
    .from('user_strain_symptoms')
    .select('strain_id, symptom:patient_symptoms(id, name, status)')
    .eq('profile_id', profile.id);
  if (error) throw error;
  const map = new Map<string, PortfolioSymptom[]>();
  for (const row of (data ?? []) as {
    strain_id: string;
    symptom: PortfolioSymptom | null;
  }[]) {
    if (!row.symptom) continue;
    const list = map.get(row.strain_id) ?? [];
    list.push(row.symptom);
    map.set(row.strain_id, list);
  }
  return map;
}

/**
 * Replace the set of symptoms tagged on a strain for the current user.
 * Diff-based: delete the dropped ids, insert the new ones, both scoped by
 * (profile_id, strain_id). RLS enforces profile ownership; the composite
 * PK makes inserts idempotent.
 */
export async function setStrainSymptoms(
  strainId: string,
  symptomIds: string[],
): Promise<void> {
  const profile = authStore.profile();
  if (!profile) throw new Error('Sign in required');
  const desired = new Set(symptomIds);

  const current = await db
    .from('user_strain_symptoms')
    .select('symptom_id')
    .eq('profile_id', profile.id)
    .eq('strain_id', strainId);
  if (current.error) throw current.error;
  const existing = new Set(
    ((current.data ?? []) as { symptom_id: string }[]).map((r) => r.symptom_id),
  );

  const toDelete = [...existing].filter((id) => !desired.has(id));
  const toInsert = [...desired].filter((id) => !existing.has(id));

  if (toDelete.length > 0) {
    const { error } = await db
      .from('user_strain_symptoms')
      .delete()
      .eq('profile_id', profile.id)
      .eq('strain_id', strainId)
      .in('symptom_id', toDelete);
    if (error) throw error;
  }

  if (toInsert.length > 0) {
    const { error } = await db.from('user_strain_symptoms').insert(
      toInsert.map((symptom_id) => ({
        profile_id: profile.id,
        strain_id: strainId,
        symptom_id,
      })),
    );
    if (error) throw error;
  }
}
